export class ValidationUtils {
    /**
     * Validates if a quantity value is valid (positive integer greater than 0)
     * @param {string|number} quantity - The quantity to validate
     * @returns {boolean} - True if quantity is valid, false otherwise
     */
    static isValidQuantity(quantity) {
        if (!quantity) return false;
        const numericValue = parseInt(quantity, 10);
        return !isNaN(numericValue) && numericValue > 0 && quantity.toString() === numericValue.toString();
    }

    /**
     * Validates if a quantity input change is acceptable
     * Allows empty string (for user typing experience) and positive integers
     * @param {string} newValue - The new input value
     * @returns {boolean} - True if the change should be accepted, false otherwise
     */
    static isAcceptableQuantityInput(newValue) {
        if (newValue === '') return true;
        return ValidationUtils.isValidQuantity(newValue);
    }

    /**
     * Returns the effective maximum quantity for an item based on max_count_per_booking.
     * Returns 0 when there is no cap (unlimited).
     * @param {Object} item - The article/product item
     * @returns {number} - The effective max quantity, or 0 if unlimited
     */
    static getEffectiveMaxQuantity(item) {
        if (!item) return 0;
        const maxPerBooking = parseInt(item.max_count_per_booking, 10);
        if (!maxPerBooking || maxPerBooking <= 0) return 0;
        const productCount = parseInt(item.product_count, 10);
        if (productCount > 0) {
            return Math.min(productCount, maxPerBooking);
        }
        return maxPerBooking;
    }

    /**
     * Checks if a quantity exceeds the max_count_per_booking limit for an item.
     * @param {string|number} quantity - The quantity to check
     * @param {Object} item - The article/product item
     * @returns {boolean} - True if quantity is within the limit (or no limit), false if exceeded
     */
    static isQuantityWithinMaxLimit(quantity, item) {
        const effectiveMax = ValidationUtils.getEffectiveMaxQuantity(item);
        if (effectiveMax === 0) return true;
        const numericValue = parseInt(quantity, 10);
        if (isNaN(numericValue)) return true;
        return numericValue <= effectiveMax;
    }

    /**
     * Checks if a cart item has a valid quantity
     * Handles different item types (return keys, configurable items, regular items)
     * @param {Object} item - The cart item to check
     * @returns {boolean} - True if item has valid quantity or doesn't require quantity validation
     */
    static hasValidCartItemQuantity(item) {
        if (item.isReturnKey) return true;
        if (item.isConfigurable && item.subarticles && Object.values(item.subarticles).length > 0) return true;
        if (!ValidationUtils.isValidQuantity(item.quantity)) return false;
        return ValidationUtils.isQuantityWithinMaxLimit(item.quantity, item);
    }

    /**
     * Checks if a cart item quantity is valid enough to run availability checks.
     * Unlike hasValidCartItemQuantity, this does not enforce max_count_per_booking
     * so the API can return the shared max_count availability state.
     * @param {Object} item - The cart item to check
     * @returns {boolean} - True if availability can be checked for this item
     */
    static hasCartItemQuantityReadyForAvailability(item) {
        if (item.isReturnKey) return true;
        if (item.isConfigurable && item.subarticles && Object.values(item.subarticles).length > 0) return true;
        return ValidationUtils.isValidQuantity(item.quantity);
    }
}
